import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { es as esLocale } from "date-fns/locale/es";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "@/styles/big-calendar-dark.css";
import { LayoutDashboard, CalendarCheck, Plus, Video, X, Clock, Users } from "lucide-react";

const NAV = [
  { to: "/app/instructor", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/app/instructor/calendario", label: "Calendario", icon: CalendarCheck },
];

const locales = { es: esLocale };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), getDay, locales });

const TYPE_COLORS = {
  live_group: "#F26A21",
  mentoria_1on1: "#0EA5E9",
  office_hours: "#10B981",
  ad_hoc: "#8B5CF6",
};

export default function InstructorCalendario() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState("week");
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/instructor/sessions").then((r) => setSessions(r.data.items || []));
    api.get("/instructor/availability").then((r) => setAvailability(r.data.items || []));
    api.get("/instructor/bookings").then((r) => setBookings(r.data.items || []));
  };
  useEffect(() => { load(); }, []);

  const events = useMemo(() => sessions.filter((s) => s.status !== "cancelled").map((s) => ({
    id: s.id,
    title: `${s.title}`,
    start: new Date(s.starts_at),
    end: new Date(new Date(s.starts_at).getTime() + s.duration_min * 60000),
    resource: s,
  })), [sessions]);

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: TYPE_COLORS[event.resource.type] || "#F26A21",
      borderColor: TYPE_COLORS[event.resource.type] || "#F26A21",
      borderRadius: 6,
      color: "#0B0B0B",
      fontWeight: 600,
      padding: "2px 6px",
    },
  });

  return (
    <DashboardLayout title="Studio del Instructor" navItems={NAV} tenantSlug="dcc">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Fase 9 · Calendario</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Mi calendario</h1>
          <p className="text-sm text-gray-400 mt-1">Sesiones live, mentorías 1:1 y disponibilidad.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="inst-new-session-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          <Plus className="w-4 h-4" /> Nueva sesión
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="p-3 rounded-2xl bg-[#111827] border border-white/10" data-testid="inst-calendar-container">
          <Calendar
            localizer={localizer}
            events={events}
            defaultView="week"
            views={["month", "week", "day"]}
            onView={setView}
            view={view}
            step={30}
            timeslots={2}
            min={new Date(2020, 0, 1, 7, 0)}
            max={new Date(2020, 0, 1, 22, 0)}
            style={{ height: 600 }}
            culture="es"
            eventPropGetter={eventPropGetter}
            onSelectEvent={(e) => setSelected(e.resource)}
            messages={{
              next: "Sig.", previous: "Ant.", today: "Hoy",
              month: "Mes", week: "Semana", day: "Día",
              date: "Fecha", time: "Hora", event: "Sesión",
              noEventsInRange: "Sin sesiones en este rango",
              allDay: "Todo el día", showMore: (n) => `+${n} más`,
            }}
          />
          <div className="mt-3 flex flex-wrap gap-3 text-xs">
            {Object.entries(TYPE_COLORS).map(([k, c]) => (
              <div key={k} className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: c }} />
                <span className="text-gray-400">{k.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <AvailabilityPanel availability={availability} onSaved={load} />
          <BookingsPanel bookings={bookings} />
        </div>
      </div>

      {creating && <NewSessionModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {selected && <SessionDetailModal session={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />}
    </DashboardLayout>
  );
}

function AvailabilityPanel({ availability, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [slots, setSlots] = useState(availability);
  useEffect(() => setSlots(availability), [availability]);
  const WEEK = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  const addSlot = () => setSlots([...slots, { weekday: 2, start_time: "16:00", end_time: "19:00", slot_duration_min: 30, is_active: true }]);
  const rem = (i) => setSlots(slots.filter((_, idx) => idx !== i));
  const upd = (i, patch) => setSlots(slots.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const save = async () => {
    try {
      await api.put("/instructor/availability", { slots });
      toast.success("Disponibilidad actualizada");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="availability-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#F26A21]" /> Disponibilidad mentorías
        </h3>
        <button onClick={() => setEditing(!editing)}
          data-testid="availability-toggle-btn"
          className="text-xs px-3 py-1 rounded-md bg-white/5 hover:bg-white/10 text-white">
          {editing ? "Cancelar" : "Editar"}
        </button>
      </div>
      {!editing && slots.length === 0 && <p className="text-xs text-gray-500">Sin disponibilidad configurada. Los alumnos no pueden reservar mentorías.</p>}
      {!editing && slots.map((s, i) => (
        <p key={i} className="text-xs text-gray-300 py-1 flex items-center gap-2" data-testid={`avail-row-${i}`}>
          <span className="text-[10px] font-mono bg-[#F26A21]/20 text-[#F26A21] px-1.5 py-0.5 rounded">{WEEK[s.weekday]}</span>
          <span>{s.start_time} → {s.end_time}</span>
          <span className="text-gray-500">· {s.slot_duration_min}min slots</span>
        </p>
      ))}
      {editing && (
        <div className="space-y-2">
          {slots.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <select value={s.weekday} onChange={(e) => upd(i, { weekday: parseInt(e.target.value) })}
                className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white">
                {WEEK.map((w, wi) => <option key={wi} value={wi}>{w}</option>)}
              </select>
              <input type="time" value={s.start_time} onChange={(e) => upd(i, { start_time: e.target.value })}
                className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white" />
              <span className="text-gray-500">→</span>
              <input type="time" value={s.end_time} onChange={(e) => upd(i, { end_time: e.target.value })}
                className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white" />
              <button onClick={() => rem(i)} className="text-red-400 p-1"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <button onClick={addSlot} className="text-xs text-[#F26A21] hover:text-[#D9541A]">+ Agregar franja</button>
          <button onClick={save} data-testid="availability-save-btn"
            className="ml-2 text-xs px-3 py-1 rounded bg-[#F26A21] text-[#0B0B0B] font-bold">Guardar</button>
        </div>
      )}
    </div>
  );
}

function BookingsPanel({ bookings }) {
  return (
    <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="bookings-panel">
      <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-[#0EA5E9]" /> Mentorías reservadas ({bookings.length})
      </h3>
      {bookings.length === 0 && <p className="text-xs text-gray-500">Sin reservas próximas.</p>}
      {bookings.map((b) => (
        <div key={b.id} className="py-2 border-t border-white/5 first:border-0" data-testid={`booking-row-${b.id}`}>
          <p className="text-xs font-semibold text-white">{b.booker_name || "Alumno"}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">{new Date(b.slot_starts_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}</p>
          {b.topic && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{b.topic}</p>}
        </div>
      ))}
    </div>
  );
}

function NewSessionModal({ onClose, onDone }) {
  const [type, setType] = useState("live_group");
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState(new Date(Date.now() + 3600000).toISOString().slice(0, 16));
  const [duration, setDuration] = useState(60);
  const [platform, setPlatform] = useState("dcc_room");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title || !startsAt) return toast.error("Título + fecha requeridos");
    setBusy(true);
    try {
      const startIso = new Date(startsAt).toISOString();
      await api.post("/instructor/sessions", {
        type, title, starts_at: startIso, duration_min: parseInt(duration),
        platform, meeting_url: platform === "dcc_room" ? undefined : meetingUrl,
      });
      toast.success("Sesión creada");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="new-session-modal"
        className="w-full max-w-md bg-[#111827] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white font-display">Nueva sesión</h2>
        <div className="mt-4 space-y-3">
          <SelField label="Tipo" val={type} on={setType} tid="ns-type"
            opts={[["live_group", "Live grupal"], ["mentoria_1on1", "Mentoría 1:1"], ["office_hours", "Office hours"], ["ad_hoc", "Ad hoc"]]} />
          <F label="Título" val={title} on={setTitle} tid="ns-title" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Fecha/hora" val={startsAt} on={setStartsAt} type="datetime-local" tid="ns-starts" />
            <F label="Duración min" val={duration} on={setDuration} tid="ns-duration" />
          </div>
          <SelField label="Plataforma" val={platform} on={setPlatform} tid="ns-platform"
            opts={[["dcc_room", "Sala DCC"], ["zoom", "Zoom"], ["meet", "Google Meet"], ["teams", "Teams"], ["presencial", "Presencial"]]} />
          {platform !== "dcc_room" && platform !== "presencial" && (
            <F label="Meeting URL" val={meetingUrl} on={setMeetingUrl} tid="ns-url" />
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">Cancelar</button>
          <button onClick={submit} disabled={busy} data-testid="ns-submit-btn"
            className="px-6 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
            {busy ? "Creando…" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionDetailModal({ session, onClose, onDone }) {
  const cancel = async () => {
    if (!window.confirm("¿Cancelar esta sesión? Los alumnos serán notificados.")) return;
    try {
      await api.delete(`/instructor/sessions/${session.id}`);
      toast.success("Sesión cancelada");
      onDone();
    } catch (e) { toast.error("Error"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="session-detail-modal"
        className="w-full max-w-md bg-[#111827] border border-white/10 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white font-display">{session.title}</h2>
        <div className="mt-3 space-y-2 text-xs text-gray-300">
          <p><span className="text-gray-500">Tipo:</span> {session.type}</p>
          <p><span className="text-gray-500">Inicio:</span> {new Date(session.starts_at).toLocaleString("es-MX")}</p>
          <p><span className="text-gray-500">Duración:</span> {session.duration_min} min</p>
          <p><span className="text-gray-500">Plataforma:</span> {session.platform}</p>
          <p><span className="text-gray-500">Asistentes:</span> {(session.attendees || []).length}</p>
          {session.meeting_url && (
            <p className="mt-2"><a href={session.meeting_url} target="_blank" rel="noreferrer" className="text-[#F26A21] underline text-xs inline-flex items-center gap-1">
              <Video className="w-3 h-3" /> {session.meeting_url}</a></p>
          )}
        </div>
        <div className="mt-5 flex justify-between gap-3">
          <button onClick={cancel} data-testid="session-cancel-btn"
            className="px-4 py-2 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10 text-sm min-h-[44px]">Cancelar sesión</button>
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-white/10 text-white text-sm min-h-[44px]">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, val, on, type = "text", tid }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{label}</label>
      <input type={type} value={val} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F26A21]" />
    </div>
  );
}

function SelField({ label, val, on, opts, tid }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{label}</label>
      <select value={val} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
